import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, Shield, Plus, LayoutDashboard, ShoppingBag, Search, User, SlidersHorizontal, Heart, Bell } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { useNotifications } from '../context/NotificationsContext';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { listingsAPI } from '../lib/api';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';

const Header = () => {
  const { t } = useTranslation();
  const { user, logout, isAuthenticated, isAdmin } = useAuth();
  const { count: favCount } = useFavorites();
  const { notifications, unreadCount, markAllRead, markRead } = useNotifications();
  const [showNotifs, setShowNotifs] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('Toutes');
  const [showCatDropdown, setShowCatDropdown] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  const categories = ['Toutes', 'Bullpadel', 'Head', 'Nox', 'Babolat', 'Adidas', 'Wilson', 'StarVie', 'Varlion', 'Siux', 'Dunlop', 'Drop Shot', 'Vibora', 'Kuikma', 'Black Crown', 'Royal Padel', 'Cartri', 'Puma'];

  const fetchSuggestions = useCallback(async (query) => {
    if (query.length < 2) { setSuggestions([]); return; }
    try {
      const params = { search: query, limit: 6 };
      if (selectedCategory !== 'Toutes') params.brand = selectedCategory.toLowerCase();
      const res = await listingsAPI.getAll(params);
      setSuggestions(res.data.listings || []);
    } catch { setSuggestions([]); }
  }, [selectedCategory]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(searchQuery), 250);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery, fetchSuggestions]);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
        setShowCatDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setShowSuggestions(false);
    setSearchQuery('');
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    const params = new URLSearchParams({ search: searchQuery });
    if (selectedCategory !== 'Toutes') params.set('brand', selectedCategory.toLowerCase());
    navigate(`/catalogue?${params.toString()}`);
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (listing) => {
    navigate(`/annonce/${listing.id}`);
    setShowSuggestions(false);
    setSearchQuery('');
  };

  return (
    <header className="sticky top-0 z-50" data-testid="header">
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          {/* Top row: logo + search + actions */}
          <div className="flex items-center gap-2 sm:gap-3 h-[68px] sm:h-20">
            {/* Logo */}
            <Link to="/" className="shrink-0 flex items-center" data-testid="logo-link">
              <img src="/assets/padsmash_logo_main.png" alt="PadSmash" className="h-20 sm:h-24 w-auto" />
            </Link>

            {/* Search bar - desktop */}
            <div className="hidden sm:block flex-1 min-w-0 relative" ref={searchRef}>
              <form onSubmit={handleSearch} className="flex items-center h-10 rounded-full bg-[#F0F7FA] border border-[#D6E8F0] transition-all px-4 gap-2" data-testid="search-bar">
                <Search className="w-4 h-4 text-[#B8D4E3] shrink-0" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
                  onFocus={() => searchQuery.length >= 2 && setShowSuggestions(true)}
                  placeholder={t('nav.search_placeholder')}
                  className="flex-1 text-sm bg-transparent text-[#033871] placeholder-[#B8D4E3] outline-none min-w-0"
                  data-testid="search-input"
                />
                <button type="submit" className="hidden" data-testid="search-submit-btn" />
              </form>

              {/* Suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E8F4FD] rounded-xl shadow-2xl overflow-hidden z-50" data-testid="search-suggestions">
                  {suggestions.map(s => (
                    <button key={s.id} onClick={() => handleSuggestionClick(s)}
                      className="flex items-center gap-3 w-full px-4 py-2.5 hover:bg-[#F7FAFB] transition-colors text-left"
                      data-testid={`suggestion-${s.id}`}>
                      <Search className="w-3.5 h-3.5 text-[#B8D4E3] shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-[#033871] truncate">{s.model}</p>
                        <p className="text-[10px] text-[#5A8BA8]">{s.brand} &middot; {s.price.toFixed(0)}&euro;</p>
                      </div>
                    </button>
                  ))}
                  <button onClick={handleSearch}
                    className="w-full px-4 py-2.5 text-left text-sm font-semibold text-[#0C63BF] hover:bg-[#F0F7FA] border-t border-[#F0F7FA] transition-colors"
                    data-testid="search-see-all">
                    {t('nav.see_all_results', { query: searchQuery })}
                  </button>
                </div>
              )}
            </div>

            {/* Search icon - mobile only */}
            <div className="flex-1 sm:hidden" />
            <button
              onClick={() => setMobileSearchOpen(true)}
              className="sm:hidden w-9 h-9 hover:bg-[#F0F7FA] rounded-lg flex items-center justify-center transition-colors"
              data-testid="mobile-search-btn"
            >
              <Search className="w-5 h-5 text-[#033871]/60" />
            </button>

            {/* Right actions */}
            <div className="flex items-center gap-1.5 shrink-0">
              {isAuthenticated && (
                <Link to="/favoris" className="relative" data-testid="header-favorites-btn">
                  <button className="w-8 h-8 sm:w-9 sm:h-9 hover:bg-[#F0F7FA] rounded-lg flex items-center justify-center transition-colors">
                    <Heart className={`w-4 h-4 sm:w-5 sm:h-5 ${favCount > 0 ? 'text-red-400 fill-red-400' : 'text-[#033871]/60'} transition-colors`} />
                  </button>
                  {favCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{favCount > 9 ? '9+' : favCount}</span>
                  )}
                </Link>
              )}
              {isAuthenticated && (
                <div className="relative" data-testid="header-notifs-btn">
                  <button onClick={() => setShowNotifs(!showNotifs)} className="w-8 h-8 sm:w-9 sm:h-9 hover:bg-[#F0F7FA] rounded-lg flex items-center justify-center transition-colors">
                    <Bell className={`w-4 h-4 sm:w-5 sm:h-5 ${unreadCount > 0 ? 'text-yellow-500' : 'text-[#033871]/60'} transition-colors`} />
                  </button>
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">{unreadCount > 9 ? '9+' : unreadCount}</span>
                  )}
                  {showNotifs && (
                    <>
                      <div className="fixed inset-0 z-[55] sm:hidden" onClick={() => setShowNotifs(false)} />
                      <div className="fixed sm:absolute left-0 right-0 sm:left-auto sm:right-0 top-[68px] sm:top-full sm:mt-2 sm:w-80 mx-2 sm:mx-0 bg-white rounded-xl shadow-2xl border border-[#E8F4FD] z-[60] overflow-hidden" data-testid="notifs-dropdown">
                      <div className="flex items-center justify-between px-4 py-3 border-b border-[#F0F7FA]">
                        <h3 className="text-sm font-bold text-[#033871]">{t('nav.notifications')}</h3>
                        {unreadCount > 0 && (
                          <button onClick={() => { markAllRead(); }} className="text-[10px] text-[#0C63BF] font-medium hover:underline" data-testid="notifs-read-all">
                            {t('nav.mark_all_read')}
                          </button>
                        )}
                      </div>
                      <div className="max-h-72 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <p className="text-sm text-[#5A8BA8] text-center py-8">{t('nav.no_notifications')}</p>
                        ) : (
                          notifications.slice(0, 10).map(n => (
                            <button key={n.id} onClick={() => { markRead(n.id); setShowNotifs(false); if (n.link) navigate(n.link); }}
                              className={`w-full text-left px-4 py-3 border-b border-[#F7FAFB] hover:bg-[#F7FAFB] transition-colors ${!n.read ? 'bg-[#F0F7FA]' : ''}`}
                              data-testid={`notif-${n.id}`}>
                              <p className={`text-sm ${!n.read ? 'font-bold text-[#033871]' : 'text-[#5A8BA8]'}`}>{n.title}</p>
                              <p className="text-[11px] text-[#B8D4E3] line-clamp-2 mt-0.5">{n.message}</p>
                            </button>
                          ))
                        )}
                      </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              <Link to="/vendre" className="hidden md:flex">
                <Button size="sm" className="bg-[#32CD32] hover:bg-[#28a428] gap-1.5 rounded-lg font-bold text-xs h-9 text-white" data-testid="header-sell-btn">
                  <Plus className="w-3.5 h-3.5" /> {t('nav.sell')}
                </Button>
              </Link>
              <LanguageSwitcher />

              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="w-8 h-8 sm:w-9 sm:h-9 bg-[#033871] hover:bg-[#021E47] rounded-lg flex items-center justify-center text-white font-bold text-sm transition-colors" data-testid="user-menu-btn">
                      {user?.first_name?.[0]?.toUpperCase()}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-white border-[#E8F4FD] rounded-xl shadow-xl p-1">
                    <div className="px-3 py-2.5 border-b border-[#F0F7FA] mb-1">
                      <p className="font-bold text-[#033871] text-sm">{user?.first_name} {user?.last_name}</p>
                      <p className="text-[10px] text-gray-400 truncate">{user?.email}</p>
                    </div>
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer rounded-lg" data-testid="nav-dashboard">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> {t('nav.my_space')}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/favoris')} className="cursor-pointer rounded-lg" data-testid="nav-favorites">
                      <Heart className="w-4 h-4 mr-2" /> {t('nav.my_favorites')} {favCount > 0 && <span className="ml-auto text-[10px] font-bold text-red-500">{favCount}</span>}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer rounded-lg" data-testid="nav-orders">
                      <ShoppingBag className="w-4 h-4 mr-2" /> {t('nav.my_purchases')}
                    </DropdownMenuItem>
                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator className="bg-[#F0F7FA]" />
                        <DropdownMenuItem onClick={() => navigate('/admin')} className="cursor-pointer text-[#0C63BF] font-semibold rounded-lg" data-testid="admin-link">
                          <Shield className="w-4 h-4 mr-2" /> {t('nav.admin')}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator className="bg-[#F0F7FA]" />
                    <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} className="cursor-pointer text-red-500 rounded-lg" data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" /> {t('nav.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/login" className="hidden sm:block">
                    <button className="text-[#033871] hover:text-[#0C63BF] text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-50 transition-colors" data-testid="login-btn">{t('nav.login')}</button>
                  </Link>
                  <Link to="/login" className="sm:hidden">
                    <button className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-colors" data-testid="login-btn-mobile">
                      <User className="w-4 h-4 text-[#033871]" />
                    </button>
                  </Link>
                </>
              )}

              <button className="md:hidden w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-[#033871]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-nav - desktop */}
      <div className="bg-white border-t border-gray-100 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1.5 h-10 -ml-3">
            {[
              { label: t('nav.all_rackets'), href: '/catalogue' },
              { label: t('nav.estimate'), href: '/estimer', highlight: true },
              { label: 'PadCoach', href: '/guide', highlight: true, isNew: true },
              { label: 'PadPro', href: '/padpro', highlight: true, isNew: true },
              { label: t('nav.blog'), href: '/blog' },
              { label: t('nav.how_it_works'), href: '/comment-ca-marche' },
            ].map(item => (
              <Link key={item.label} to={item.href}
                className={`px-3 py-1.5 text-sm font-medium rounded transition-colors hover:bg-gray-50 whitespace-nowrap flex items-center gap-1.5 ${item.highlight ? 'text-[#0C63BF] hover:text-[#033871]' : 'text-gray-500 hover:text-[#033871]'}`}
                data-testid={`subnav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                {item.label}
                {item.isNew && <span className="bg-[#32CD32] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">NEW</span>}
              </Link>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-3 space-y-1">
            <Link to="/catalogue" className="block py-2.5 px-3 rounded-lg text-[#033871] font-semibold text-sm hover:bg-gray-50" onClick={() => setMobileMenuOpen(false)}>{t('nav.all_rackets')}</Link>
            <Link to="/vendre" className="block py-2.5 px-3 rounded-lg text-[#32CD32] font-semibold text-sm hover:bg-gray-50" onClick={() => setMobileMenuOpen(false)}>{t('nav.sell_my_racket')}</Link>
            <Link to="/estimer" className="block py-2.5 px-3 rounded-lg text-[#0C63BF] font-semibold text-sm hover:bg-gray-50" onClick={() => setMobileMenuOpen(false)}>{t('nav.estimate')}</Link>
            <Link to="/guide" className="block py-2.5 px-3 rounded-lg text-[#0C63BF] font-semibold text-sm hover:bg-gray-50 flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
              PadCoach <span className="bg-[#32CD32] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">NEW</span>
            </Link>
            <Link to="/padpro" className="block py-2.5 px-3 rounded-lg text-[#0C63BF] font-semibold text-sm hover:bg-gray-50 flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
              PadPro <span className="bg-[#32CD32] text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">NEW</span>
            </Link>
            <Link to="/blog" className="block py-2.5 px-3 rounded-lg text-[#033871] font-semibold text-sm hover:bg-gray-50" onClick={() => setMobileMenuOpen(false)}>
              {t('nav.blog')}
            </Link>
            <Link to="/comment-ca-marche" className="block py-2.5 px-3 rounded-lg text-[#033871] font-semibold text-sm hover:bg-gray-50" onClick={() => setMobileMenuOpen(false)}>
              {t('nav.how_it_works')}
            </Link>
            {isAuthenticated && (
              <div className="border-t border-gray-100 pt-2 mt-2">
                <Link to="/dashboard" className="block py-2.5 px-3 rounded-lg text-[#033871] font-semibold text-sm hover:bg-gray-50" onClick={() => setMobileMenuOpen(false)}>{t('nav.my_space')}</Link>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Mobile search overlay */}
      {mobileSearchOpen && (
        <div className="sm:hidden fixed inset-0 z-[60] bg-white" data-testid="mobile-search-overlay">
          <div className="flex items-center gap-2 px-3 h-14 border-b border-[#E8F4FD]">
            <button onClick={() => { setMobileSearchOpen(false); setShowSuggestions(false); }} className="w-9 h-9 flex items-center justify-center shrink-0" data-testid="mobile-search-close">
              <X className="w-5 h-5 text-[#033871]" />
            </button>
            <form onSubmit={(e) => { handleSearch(e); setMobileSearchOpen(false); }} className="flex-1 flex items-center h-10 rounded-full bg-[#F0F7FA] px-4 gap-2">
              <Search className="w-4 h-4 text-[#B8D4E3] shrink-0" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
                placeholder={t('nav.search_placeholder')}
                className="flex-1 text-sm bg-transparent text-[#033871] placeholder-[#B8D4E3] outline-none"
                autoFocus
                data-testid="mobile-search-input"
              />
            </form>
          </div>
          {showSuggestions && suggestions.length > 0 && (
            <div className="overflow-y-auto" data-testid="mobile-search-suggestions">
              {suggestions.map(s => (
                <button key={s.id} onClick={() => { handleSuggestionClick(s); setMobileSearchOpen(false); }}
                  className="flex items-center gap-3 w-full px-4 py-3 hover:bg-[#F7FAFB] transition-colors text-left border-b border-[#F0F7FA]">
                  <Search className="w-4 h-4 text-[#B8D4E3] shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#033871] truncate">{s.model}</p>
                    <p className="text-xs text-[#5A8BA8]">{s.brand} &middot; {s.price.toFixed(0)}&euro;</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </header>
  );
};

export default Header;
